export interface SampleRequiredView {
  orderType?: "From Supplier" | "To Customer";
  supplierId?: string | null;
  customerId?: string | null;
  deliveryDate?: number | null;
  teamDepartment?: string | null;
  items?: unknown[];
}

const empty = (v: unknown) => v === null || v === undefined || (typeof v === "string" && !v.trim());

/**
 * Ràng buộc bắt buộc của một đơn mẫu.
 *
 * `touched` = tập trường mà người dùng thực sự gửi lên trong lần sửa này.
 *
 * VÌ SAO KHÔNG ÉP CỨNG MỌI TRƯỜNG KHI SỬA: trên dữ liệu thật có 18/283 đơn
 * không có team/phòng ban và 6 đơn "From Supplier" không có nhà cung cấp — đó
 * là đơn cũ di trú sang. Bắt buộc vô điều kiện thì 24 đơn đó KHÔNG CÒN MỞ RA
 * SỬA ĐƯỢC, kể cả khi người dùng vào chính là để bổ sung chỗ thiếu.
 *
 * Nên: tạo mới thì bắt buộc đủ; sửa thì cấm LÀM RỖNG một trường bắt buộc,
 * còn trường vốn đã rỗng từ trước thì để yên. Số dòng luôn phải ≥ 1 (không đơn
 * nào trên production có 0 dòng, nên quy tắc này không khoá dữ liệu cũ).
 *
 * `before` = giá trị ĐANG LƯU của bản ghi, và nó chính là thứ quyết định câu
 * "vốn đã rỗng từ trước". Trước đây quy tắc này dựa vào `touched`, tức là suy
 * ra ý định từ chỗ trường có mặt trong payload hay không — mà điều đó chỉ đúng
 * chừng nào giao diện còn bỏ hẳn ô trống ra khỏi payload. Ngay khi giao diện
 * bắt đầu gửi chuỗi rỗng để nói "tôi vừa xoá ô này" (đúng việc phải làm, xem
 * SampleOrderEditorPage), phép suy luận đó sụp: 18 đơn di trú không có phòng
 * ban sẽ bị 400 ngay khi bấm Lưu, kể cả khi người dùng không hề đụng vào ô đó.
 * So thẳng với giá trị cũ thì không phụ thuộc vào cách giao diện đóng gói nữa.
 */
export function checkSampleRequired(
  v: SampleRequiredView,
  mode: "create" | "update",
  touched: ReadonlySet<string> = new Set(Object.keys(v)),
  before?: SampleRequiredView,
): string | null {
  const need = (field: string, value: unknown) => {
    if (mode === "create") return empty(value);
    if (!touched.has(field) || !empty(value)) return false;
    // Chỉ chặn khi người dùng đang XOÁ một giá trị vốn có. Không biết giá trị
    // cũ (người gọi không truyền `before`) thì giữ nguyên hành vi cũ.
    return before ? !empty(before[field as keyof SampleRequiredView]) : true;
  };
  if (touched.has("items") || mode === "create") {
    if (!v.items || v.items.length === 0) return "items";
  }
  if (need("deliveryDate", v.deliveryDate)) return "deliveryDate";
  if (need("teamDepartment", v.teamDepartment)) return "teamDepartment";
  const type = v.orderType ?? "From Supplier";
  // Đơn đặt mẫu từ NCC không có NCC, hoặc đơn gửi mẫu cho khách không có khách,
  // là chứng từ không gửi được cho ai.
  if (type === "From Supplier") {
    if (need("supplierId", v.supplierId) || (mode === "update" && touched.has("orderType") && (!before || before.orderType !== type) && empty(v.supplierId)))
      return "supplierId";
  } else if (need("customerId", v.customerId) || (mode === "update" && touched.has("orderType") && (!before || before.orderType !== type) && empty(v.customerId))) {
    return "customerId";
  }
  return null;
}


/** Identity is the pair, including an explicitly blank variant. */
export function sampleIdentity(code: string | null | undefined, subRef?: string | null): string {
  return JSON.stringify([(code ?? "").trim().toLowerCase(), (subRef ?? "").trim().toLowerCase()]);
}
